/**
 * Linguagem do contexto visits no backoffice — espelha `visits/domain`.
 *
 * O profissional pede, no portal, a visita de um analista; o time de
 * relacionamento chama no WhatsApp, agenda, conclui ou cancela aqui. As
 * regras abaixo só avisam antes do clique; quem decide é a API.
 */
import { z } from 'zod';
import type { components } from '@/core/http';
import type { BadgeTone } from '@/core/ui';

export type Visit = components['schemas']['VisitOut'];
export type VisitPage = components['schemas']['VisitPageOut'];
export type ScheduleBody = components['schemas']['ScheduleIn'];

export const VisitStatus = {
  REQUESTED: 'requested',
  CONTACTING: 'contacting',
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;
export type VisitStatusCode = (typeof VisitStatus)[keyof typeof VisitStatus];

/** Na ordem do ciclo de vida — é a ordem do filtro. */
export const STATUSES: { value: VisitStatusCode; label: string }[] = [
  { value: 'requested', label: 'Novos' },
  { value: 'contacting', label: 'Em contato' },
  { value: 'scheduled', label: 'Agendadas' },
  { value: 'completed', label: 'Concluídas' },
  { value: 'cancelled', label: 'Canceladas' },
];

export const STATUS_LABELS: Record<string, string> = {
  requested: 'Novo pedido',
  contacting: 'Em contato',
  scheduled: 'Agendada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

export const STATUS_TONES: Record<string, BadgeTone> = {
  requested: 'brand',
  contacting: 'warning',
  scheduled: 'success',
  completed: 'neutral',
  cancelled: 'danger',
};

export function isStatus(v: unknown): v is VisitStatusCode {
  return STATUSES.some((s) => s.value === v);
}

// ──────────────────────────── O que cada passo permite ────────────────────────────
// Espelha as transições do agregado VisitRequest.

export const canMarkContacting = (v: Pick<Visit, 'status'>) => v.status === VisitStatus.REQUESTED;
export const canSchedule = (v: Pick<Visit, 'status'>) =>
  v.status === VisitStatus.REQUESTED ||
  v.status === VisitStatus.CONTACTING ||
  v.status === VisitStatus.SCHEDULED;
export const canComplete = (v: Pick<Visit, 'status'>) => v.status === VisitStatus.SCHEDULED;
export const canCancel = (v: Pick<Visit, 'can_cancel'>) => v.can_cancel;

// ──────────────────────────── Disponibilidade ────────────────────────────

export const WEEKDAYS = [
  { value: 'mon', short: 'Seg', label: 'Segunda' },
  { value: 'tue', short: 'Ter', label: 'Terça' },
  { value: 'wed', short: 'Qua', label: 'Quarta' },
  { value: 'thu', short: 'Qui', label: 'Quinta' },
  { value: 'fri', short: 'Sex', label: 'Sexta' },
  { value: 'sat', short: 'Sáb', label: 'Sábado' },
] as const;

export const PERIODS = [
  { value: 'morning', label: 'Manhã' },
  { value: 'afternoon', label: 'Tarde' },
  { value: 'evening', label: 'Noite' },
] as const;

/** O horário está entre os que a pessoa marcou? (para a grade do detalhe) */
export function hasSlot(v: Pick<Visit, 'availability'>, weekday: string, period: string): boolean {
  return v.availability.some((s) => s.weekday === weekday && s.period === period);
}

/** "Seg manhã e tarde · Qua noite" — a coluna de disponibilidade da lista. */
export function availabilityText(slots: readonly { weekday: string; period: string }[]): string {
  const byDay = new Map<string, string[]>();
  for (const d of WEEKDAYS) {
    const periods = PERIODS.filter((p) => slots.some((s) => s.weekday === d.value && s.period === p.value));
    if (periods.length)
      byDay.set(
        d.short,
        periods.map((p) => p.label.toLowerCase()),
      );
  }
  return [...byDay.entries()].map(([day, periods]) => `${day} ${joinPt(periods)}`).join(' · ');
}

function joinPt(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items.at(-1)}`;
}

// ──────────────────────────── Datas ────────────────────────────

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});
const shortDate = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

/** "qua., 22/10, 14:00" — a data da visita. */
export const formatVisitWhen = (iso: string) => dateTime.format(new Date(iso));

/** "16 de out. de 2026". */
export const formatDate = (iso: string) => shortDate.format(new Date(iso));

/** O "a partir de" (AAAA-MM-DD) como data local, sem pular de dia pelo fuso. */
export const formatDay = (day: string) => shortDate.format(new Date(`${day}T12:00:00`));

/** "Campinas/SP", "SP", "Campinas" ou "—". */
export function placeText(v: Pick<Visit, 'city' | 'uf'>): string {
  return [v.city, v.uf].filter(Boolean).join('/') || '—';
}

export const MODALITIES = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'online', label: 'Online' },
] as const;

export const modalityLabel = (m: string | null | undefined) =>
  MODALITIES.find((x) => x.value === m)?.label ?? m ?? '';

// ──────────────────────────── Formulários ────────────────────────────

/** Data e hora locais (dos campos `date` e `time`) → ISO com fuso. */
export function toIsoWithZone(date: string, time: string): string {
  return new Date(`${date}T${time}:00`).toISOString();
}

/** O inverso, para abrir o "reagendar" já preenchido. */
export function splitLocal(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Limites idênticos a `ScheduleIn` (API). */
export const scheduleSchema = z
  .object({
    date: z.string().min(1, 'Escolha o dia'),
    time: z.string().min(1, 'Escolha o horário'),
    analyst_name: z.string().trim().min(2, 'Quem é o analista?').max(120, 'No máximo 120 caracteres'),
    modality: z.enum(['presencial', 'online']),
    location: z.string().trim().max(500, 'No máximo 500 caracteres'),
    team_note: z.string().trim().max(1000, 'No máximo 1000 caracteres'),
  })
  .refine((v) => !v.date || !v.time || new Date(`${v.date}T${v.time}:00`).getTime() > Date.now(), {
    message: 'A visita precisa ser num horário futuro',
    path: ['time'],
  });
export type ScheduleForm = z.infer<typeof scheduleSchema>;

export function toScheduleBody(form: ScheduleForm): ScheduleBody {
  return {
    scheduled_for: toIsoWithZone(form.date, form.time),
    analyst_name: form.analyst_name.trim(),
    modality: form.modality,
    location: form.location.trim(),
    team_note: form.team_note.trim(),
  };
}

export const cancelSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(3, 'Conte o motivo (a pessoa não vê este texto)')
    .max(500, 'No máximo 500 caracteres'),
});
export type CancelForm = z.infer<typeof cancelSchema>;
